'use client';

/**
 * CookbookPicker — anchored dropdown for saving a recipe to one or more
 * cookbooks. Replaces the centred AddToCookbookModal: the same list / create /
 * toggle logic, surfaced as a lightweight popover anchored to its trigger
 * instead of a full-screen modal.
 *
 * Rendered via a portal at measured fixed coordinates so it escapes the
 * `overflow: hidden` on recipe cards (and any transformed grid ancestors).
 * Flips above the trigger when there isn't room below; recomputes on
 * scroll / resize. Closes on Escape, outside click, or the close button —
 * staying open across toggles so several cookbooks can be picked in one go.
 */

import {
  useActionState,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { useTranslateActionError } from '@/components/ui/action-error';
import { useToast } from '@/components/ui/toast';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import {
  addRecipeToCookbook,
  createCookbookAndAddRecipe,
  removeRecipeFromCookbook,
  getCookbooksForModal,
  type CookbookModalItem,
} from '@/lib/actions/cookbooks';
import type { VariantType } from '@/db/schema/enums';
import styles from './cookbook-picker.module.css';

type AddState = { success: true; cookbookId?: string } | { success: false; error: string } | null;
type CreateState =
  | { success: true; cookbookId?: string; title?: string }
  | { success: false; error: string }
  | null;
type RemoveState = { success: true; cookbookId?: string } | { success: false; error: string } | null;

type Align = 'start' | 'end';

interface PopoverCoords {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
}

interface CookbookPickerProps {
  recipeId: string;
  /** The variant the user is currently viewing — persisted with the entry. */
  variantType?: VariantType;
  /**
   * Pre-fetched cookbooks (recipe-detail page fetches these server-side). When
   * omitted, the list is lazily fetched the first time the popover opens
   * (recipe cards take this path).
   */
  initialCookbooks?: CookbookModalItem[];
  /** Horizontal anchoring relative to the trigger. */
  align?: Align;
  /** Extra class for the trigger button (keeps each call site's styling). */
  triggerClassName?: string;
  /** Accessible label + tooltip for the trigger. */
  triggerLabel: string;
  /** Called when the popover opens (e.g. to dismiss a parent menu). */
  onOpen?: () => void;
  /** Trigger inner content (icon, or icon + label). */
  children: ReactNode;
}

const POPOVER_WIDTH = 288;
const VIEWPORT_MARGIN = 8;
const GAP = 8;
const EST_HEIGHT = 320;
const MAX_HEIGHT = 360;
const MIN_HEIGHT = 160;

export function CookbookPicker({
  recipeId,
  variantType,
  initialCookbooks,
  align = 'start',
  triggerClassName,
  triggerLabel,
  onOpen,
  children,
}: CookbookPickerProps) {
  const t = useTranslations('cookbook.modal');
  const tCookbook = useTranslations('cookbook');
  const tCommon = useTranslations('common');
  const tErr = useTranslateActionError();
  const { toast } = useToast();
  const titleFieldId = useId();

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<PopoverCoords | null>(null);
  const [cookbookList, setCookbookList] = useState<CookbookModalItem[] | null>(
    initialCookbooks ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [addedIds, setAddedIds] = useState<Set<string>>(
    new Set((initialCookbooks ?? []).filter((cb) => cb.containsRecipe).map((cb) => cb.id)),
  );
  // The cookbook row whose add/remove is currently in flight — drives the
  // per-row spinner while the Server Action round-trips.
  const [pendingId, setPendingId] = useState<string | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // useActionState keeps returning the SAME success object across re-renders, so
  // the toast/mirror effects must process each result exactly once — otherwise a
  // later re-render (new cookbook list, new toast identity, etc.) replays a stale
  // success, stacking a phantom toast and, for remove, un-checking the row.
  const handledAddRef = useRef<AddState>(null);
  const handledCreateRef = useRef<CreateState>(null);
  const handledRemoveRef = useRef<RemoveState>(null);

  const [addState, addAction, addPending] = useActionState<AddState, FormData>(
    addRecipeToCookbook,
    null,
  );
  const [createState, createAction, createPending] = useActionState<CreateState, FormData>(
    createCookbookAndAddRecipe,
    null,
  );
  const [removeState, removeAction, removePending] = useActionState<RemoveState, FormData>(
    removeRecipeFromCookbook,
    null,
  );

  // Compute fixed popover coordinates from the trigger rect. Anchors below by
  // default; flips above when there isn't room. Clamps within the viewport —
  // including max-height, so the panel scrolls internally instead of running
  // past the viewport edge (which put the create-form buttons off-screen).
  const computeCoords = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(POPOVER_WIDTH, vw - VIEWPORT_MARGIN * 2);

    let left = align === 'end' ? rect.right - width : rect.left;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - width - VIEWPORT_MARGIN));

    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < EST_HEIGHT && spaceAbove > spaceBelow;
    const available = (openUp ? spaceAbove : spaceBelow) - GAP - VIEWPORT_MARGIN;
    const maxHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, available));

    setCoords(
      openUp
        ? { left, width, maxHeight, bottom: vh - rect.top + GAP }
        : { left, width, maxHeight, top: rect.bottom + GAP },
    );
  }, [align]);

  function handleToggle() {
    if (open) {
      setOpen(false);
      return;
    }
    onOpen?.();
    setShowCreate(false);
    // Position from the trigger rect before opening so the portal renders
    // already placed (it only mounts once `coords` is set).
    computeCoords();
    setOpen(true);
    // Lazily fetch the cookbook list on first open when not pre-supplied.
    if (cookbookList === null && !loading) {
      setLoading(true);
      getCookbooksForModal(recipeId).then((result) => {
        const rows = result.success ? result.cookbooks : [];
        setCookbookList(rows);
        setAddedIds(new Set(rows.filter((cb) => cb.containsRecipe).map((cb) => cb.id)));
        setLoading(false);
      });
    }
  }

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Reposition on scroll / resize, close on Escape, and move focus in.
  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => computeCoords();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('keydown', onKey);
    const focusId = requestAnimationFrame(() => popoverRef.current?.focus());
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(focusId);
    };
  }, [open, computeCoords, close]);

  // When a recipe-detail re-render hands new server data, re-sync the list.
  const [prevInitial, setPrevInitial] = useState(initialCookbooks);
  if (initialCookbooks && prevInitial !== initialCookbooks) {
    setPrevInitial(initialCookbooks);
    setCookbookList(initialCookbooks);
    setAddedIds(
      new Set(initialCookbooks.filter((cb) => cb.containsRecipe).map((cb) => cb.id)),
    );
  }

  // Server-action success ⇒ update the local mirror + fire an app-level toast
  // (the standard, prominent feedback surface — consistent with Share, and it
  // survives the popover closing). Mirrors the useActionState-transition pattern.
  useEffect(() => {
    if (addState === handledAddRef.current) return;
    handledAddRef.current = addState;
    const id = addState?.success ? addState.cookbookId : undefined;
    if (id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAddedIds((prev) => new Set([...prev, id]));
      const cb = cookbookList?.find((c) => c.id === id);
      if (cb) toast(t('savedTo', { title: cb.title }), { variant: 'success' });
    }
  }, [addState, cookbookList, t, toast]);

  useEffect(() => {
    if (createState === handledCreateRef.current) return;
    handledCreateRef.current = createState;
    if (createState?.success) {
      const id = createState.cookbookId;
      const title = createState.title;
      if (id && title) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCookbookList((prev) => [
          ...(prev ?? []),
          { id, title, isDefault: false, containsRecipe: true },
        ]);
        setAddedIds((prev) => new Set([...prev, id]));
        toast(t('savedTo', { title }), { variant: 'success' });
      }
      setShowCreate(false);
    }
  }, [createState, t, toast]);

  // Clear the row spinner once neither action is in flight (success or failure).
  useEffect(() => {
    if (!addPending && !removePending) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingId(null);
    }
  }, [addPending, removePending]);

  useEffect(() => {
    if (removeState === handledRemoveRef.current) return;
    handledRemoveRef.current = removeState;
    const id = removeState?.success ? removeState.cookbookId : undefined;
    if (id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      const cb = cookbookList?.find((c) => c.id === id);
      if (cb) toast(t('removedFrom', { title: cb.title }));
    }
  }, [removeState, cookbookList, t, toast]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        onClick={handleToggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-pressed={addedIds.size > 0}
        data-saved={addedIds.size > 0 ? 'true' : undefined}
        aria-label={triggerLabel}
        title={triggerLabel}
      >
        {children}
      </button>

      {open &&
        coords &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div className={styles.scrim} aria-hidden="true" onClick={() => setOpen(false)} />
            <div
              ref={popoverRef}
              className={styles.popover}
              role="dialog"
              aria-label={t('title')}
              tabIndex={-1}
              style={{
                left: coords.left,
                width: coords.width,
                maxHeight: coords.maxHeight,
                top: coords.top,
                bottom: coords.bottom,
              }}
            >
              <div className={styles.header}>
                <span className={styles.headerTitle}>{t('title')}</span>
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={close}
                  aria-label={t('close')}
                >
                  <Icon name="x" size={16} strokeWidth={2} />
                </button>
              </div>

              <div className={styles.scrollArea}>
                {addState && !addState.success && (
                  <p className={styles.error} role="alert">
                    {tErr(addState.error)}
                  </p>
                )}
                {removeState && !removeState.success && (
                  <p className={styles.error} role="alert">
                    {tErr(removeState.error)}
                  </p>
                )}

                {loading ? (
                  <p className={styles.emptyText}>{tCommon('loading')}</p>
                ) : (cookbookList?.length ?? 0) === 0 ? (
                  <p className={styles.emptyText}>{t('empty')}</p>
                ) : (
                  <div className={styles.list}>
                    {cookbookList?.map((cb) => {
                      const wasAdded = addedIds.has(cb.id);
                      // One form per row — the checkbox toggles membership:
                      // checked submits removeAction, unchecked submits addAction.
                      return (
                        <form
                          key={cb.id}
                          action={wasAdded ? removeAction : addAction}
                          className={styles.row}
                        >
                          <input type="hidden" name="cookbookId" value={cb.id} />
                          <input type="hidden" name="recipeId" value={recipeId} />
                          {!wasAdded && variantType && (
                            <input type="hidden" name="variantType" value={variantType} />
                          )}
                          <span className={styles.name}>{cb.title}</span>
                          <button
                            type="submit"
                            className={styles.checkbox}
                            data-checked={wasAdded}
                            disabled={wasAdded ? removePending : addPending}
                            aria-pressed={wasAdded}
                            aria-label={
                              wasAdded
                                ? t('removeFromAria', { title: cb.title })
                                : t('addToAria', { title: cb.title })
                            }
                            onClick={() => setPendingId(cb.id)}
                          >
                            {pendingId === cb.id && (addPending || removePending) ? (
                              <Icon name="loading" size={18} className={styles.spinner} aria-hidden="true" />
                            ) : wasAdded ? (
                              <Icon name="check" size={18} strokeWidth={3} />
                            ) : null}
                          </button>
                        </form>
                      );
                    })}
                  </div>
                )}
              </div>

              {!loading && (
                <div className={styles.footer}>
                  {showCreate ? (
                    <form action={createAction} className={styles.createForm}>
                      {createState && !createState.success && (
                        <p className={styles.error} role="alert">
                          {tErr(createState.error)}
                        </p>
                      )}
                      <input type="hidden" name="recipeId" value={recipeId} />
                      {variantType && (
                        <input type="hidden" name="variantType" value={variantType} />
                      )}
                      <Field htmlFor={titleFieldId} label={t('cookbookNamePlaceholder')} required>
                        <Input
                          id={titleFieldId}
                          name="title"
                          placeholder={t('cookbookNamePlaceholder')}
                          maxLength={100}
                          autoFocus
                          disabled={createPending}
                        />
                      </Field>
                      <div className={styles.createActions}>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowCreate(false)}
                          disabled={createPending}
                        >
                          {t('cancel')}
                        </Button>
                        <Button type="submit" size="sm" loading={createPending}>
                          {createPending ? t('creating') : t('createAndSave')}
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <button
                      type="button"
                      className={styles.newBtn}
                      onClick={() => setShowCreate(true)}
                    >
                      {tCookbook('newCookbookButton')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
